import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { contentAPI, tenantAPI } from '../../services/api';
import { formatMarathiCurrency } from '../../utils/marathi';
import { LocationPicker, normalizeLocation } from '../../components/maps/LocationMap';
import GalleryAdminPanel from '../../components/admin/GalleryAdminPanel';
import AdminContactMessages from './AdminContactMessages';

// System pages: cannot be deleted (only published/unpublished or edited)
const SYSTEM_SLUGS = new Set(['home', 'about', 'services', 'schemes', 'gallery', 'contact', 'notice', 'representatives', 'banners']);

const FIXED_HERO_PAGES = [
  { key: 'about', title: 'आमच्याबद्दल (About Us)', route: '/about', defaultImage: '/HeaderBgImages/1.png' },
  { key: 'departments', title: 'विभाग (Departments)', route: '/departments', defaultImage: '/HeaderBgImages/8.png' },
  { key: 'projects', title: 'विकास कामे (Projects / Development Works)', route: '/projects', defaultImage: '/HeaderBgImages/6.png' },
  { key: 'gramsabha', title: 'ग्रामसभा (Gramsabha)', route: '/gramsabha', defaultImage: '/HeaderBgImages/6.png' },
  { key: 'schemes', title: 'योजना (Schemes)', route: '/schemes', defaultImage: '/HeaderBgImages/5.png' },
  { key: 'services', title: 'सेवा (Services)', route: '/services', defaultImage: '/HeaderBgImages/13.png' },
  { key: 'taxes', title: 'कर (Taxes / My Taxes)', route: '/my-taxes', defaultImage: '/HeaderBgImages/16.png' },
  { key: 'complaints', title: 'तक्रार (Complaints)', route: '/complaints', defaultImage: '/HeaderBgImages/4.png' },
  { key: 'notices', title: 'नोटीस (Notices)', route: '/notices', defaultImage: '/HeaderBgImages/13.png' },
  { key: 'gallery', title: 'गॅलरी (Gallery)', route: '/gallery', defaultImage: '/HeaderBgImages/9.png' },
  { key: 'facilities', title: 'सुविधा (Facilities)', route: '/facilities', defaultImage: '/HeaderBgImages/15.png' },
  { key: 'staff', title: 'कर्मचारी (Staff Directory)', route: '/staff', defaultImage: '/HeaderBgImages/12.png' },
  { key: 'businesses', title: 'व्यवसाय (Businesses)', route: '/businesses', defaultImage: '/HeaderBgImages/11.png' },
  { key: 'contact', title: 'संपर्क (Contact)', route: '/contact', defaultImage: '/HeaderBgImages/8.png' },
];

const BLANK_REP = { name: '', designation: '', photo: '', category: 'other' };
const IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const REP_CATEGORIES = ['CM', 'Deputy CM', 'Minister', 'MP', 'MLA', 'Sarpanch', 'Deputy Sarpanch', 'Member', 'other'];
const EXEMPTION_KEYS = ['senior_citizen', 'bpl', 'disability', 'sc_st', 'widow'];

const fmtExKey = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function formatExemptions(ex) {
  if (!ex) return null;
  let obj = ex;
  if (typeof ex === 'string') { try { obj = JSON.parse(ex); } catch { return ex; } }
  if (typeof obj !== 'object') return String(obj);
  const parts = Object.entries(obj).filter(([, v]) => v).map(([k, v]) => `${fmtExKey(k)}: ${v}%`);
  return parts.length ? parts.join(' · ') : null;
}

// ── Inline Rich Text Editor ───────────────────────────────────────────────
// ── Toast Notification Component ────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'linear-gradient(135deg, #27ae60, #1e8449)' : 'linear-gradient(135deg, #e74c3c, #c0392b)';
  const icon = type === 'success' ? '✅' : '❌';

  return (
    <div style={{
      position: 'fixed', top: 24, right: 24, zIndex: 10000,
      background: bgColor, color: 'white',
      padding: '16px 24px', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', gap: 12,
      fontSize: '0.95rem', fontWeight: 600,
      animation: 'toastSlideIn 0.3s ease',
      maxWidth: 400,
    }}>
      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.1rem', marginLeft: 8, opacity: 0.8 }}>✕</button>
      <style>{`@keyframes toastSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ── Custom Image URL Modal ──────────────────────────────────────────────────
function ImageUrlModal({ onSubmit, onClose }) {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleInsert = () => {
    if (url.trim()) { onSubmit(url.trim()); onClose(); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: '28px 32px', width: '100%', maxWidth: 480, boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', color: '#1a5276', fontSize: '1.1rem' }}>🔗 प्रतिमा URL टाका</h3>
        <p style={{ margin: '0 0 16px', color: '#888', fontSize: '0.85rem' }}>प्रतिमेचा URL येथे प्रविष्ट करा (https://...)</p>
        <div>
          <input
            ref={inputRef}
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{ width: '100%', padding: '12px 14px', fontSize: '0.95rem', border: '2px solid #d0d8e0', borderRadius: 8, boxSizing: 'border-box', outline: 'none', transition: 'border 0.2s' }}
            onFocus={e => { e.target.style.borderColor = '#1a5276'; }}
            onBlur={e => { e.target.style.borderColor = '#d0d8e0'; }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInsert(); } }}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 22px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>रद्द करा</button>
            <button type="button" onClick={handleInsert} disabled={!url.trim()} style={{ padding: '10px 22px', background: url.trim() ? '#1a5276' : '#ccc', color: 'white', border: 'none', borderRadius: 8, cursor: url.trim() ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '0.9rem' }}>प्रतिमा टाका</button>
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}

// ── Custom Table Insert Modal ────────────────────────────────────────────────
function TableModal({ onSubmit, onClose }) {
  const [rows, setRows] = useState(4);
  const [cols, setCols] = useState(3);
  const [hasHeader, setHasHeader] = useState(true);

  const handleInsert = () => {
    if (rows > 0 && cols > 0) { onSubmit({ rows: hasHeader ? rows + 1 : rows, cols, hasHeader }); onClose(); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.2s ease' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: '28px 32px', width: '100%', maxWidth: 400, boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', color: '#1a5276', fontSize: '1.1rem' }}>▇ तक्ता टाका</h3>
        <p style={{ margin: '0 0 16px', color: '#888', fontSize: '0.85rem' }}>तक्त्याचे ओळी आणि स्तंभ संख्या निवडा</p>
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>डेटा ओळी</label>
              <input type="number" min="1" max="50" value={rows} onChange={e => setRows(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', fontSize: '1rem', border: '2px solid #d0d8e0', borderRadius: 8, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>स्तंभ</label>
              <input type="number" min="1" max="20" value={cols} onChange={e => setCols(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 12px', fontSize: '1rem', border: '2px solid #d0d8e0', borderRadius: 8, boxSizing: 'border-box' }} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: '#444', marginBottom: 18, cursor: 'pointer' }}>
            <input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#e67e22' }} />
            शीर्षक ओळ (हेडर) जोडा
          </label>
          {/* Preview */}
          <div style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 16, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <tbody>
                {Array.from({ length: Math.min(hasHeader ? rows + 1 : rows, 5) }).map((_, r) => (
                  <tr key={r}>
                    {Array.from({ length: Math.min(cols, 6) }).map((_, c) => {
                      const isHead = hasHeader && r === 0;
                      const Tag = isHead ? 'th' : 'td';
                      return <Tag key={c} style={{ border: '1px solid #ddd', padding: '4px 8px', background: isHead ? '#e67e22' : r % 2 === 0 ? '#fff' : '#fafafa', color: isHead ? 'white' : '#444', textAlign: 'left', fontWeight: isHead ? 700 : 400 }}>{isHead ? `स्तंभ ${c + 1}` : '—'}</Tag>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {(hasHeader ? rows + 1 : rows) > 5 && <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.7rem', margin: '4px 0 0' }}>...आणखी ओळी</p>}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 22px', background: '#f0f0f0', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>रद्द करा</button>
            <button type="button" onClick={handleInsert} style={{ padding: '10px 22px', background: '#e67e22', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>तक्ता टाका</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RichTextEditor({ value, onChange, placeholder = 'येथे टाइप करा...', tenantSlug }) {
  const ref = useRef(null);
  const init = useRef(false);
  const fileInputRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImageUrlModal, setShowImageUrlModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [activeTable, setActiveTable] = useState(null);
  const [activeCell, setActiveCell] = useState(null);
  const [tableToolbarRect, setTableToolbarRect] = useState(null);

  useEffect(() => {
    if (ref.current && !init.current && value) {
      ref.current.innerHTML = value;
      init.current = true;
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    ref.current.focus();
    onChange(ref.current.innerHTML);
  };

  const insertImageAtCursor = (url) => {
    if (ref.current) {
      ref.current.focus();
      const img = `<img src="${url}" alt="image" style="max-width:100%;height:auto;border-radius:6px;margin:8px 0;" />`;
      document.execCommand('insertHTML', false, img);
      onChange(ref.current.innerHTML);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !tenantSlug) return;
    e.target.value = '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await contentAPI.uploadImage(tenantSlug, formData);
      if (res.data?.url) insertImageAtCursor(res.data.url);
    } catch (err) {
      alert('प्रतिमा अपलोड अयशस्वी: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleInsertImageUrl = () => {
    setShowImageUrlModal(true);
  };

  const insertTable = ({ rows, cols, hasHeader }) => {
    if (!ref.current) return;
    ref.current.focus();
    let html = '<table style="width:100%;border-collapse:collapse;margin:12px 0;">';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        const isHead = hasHeader && r === 0;
        const tag = isHead ? 'th' : 'td';
        const style = isHead
          ? 'style="border:1px solid #ddd;padding:10px 14px;background:#e67e22;color:white;font-weight:700;text-align:left;"'
          : 'style="border:1px solid #ddd;padding:10px 14px;"';
        html += `<${tag} ${style}>&nbsp;</${tag}>`;
      }
      html += '</tr>';
    }
    html += '</table><p><br></p>';
    document.execCommand('insertHTML', false, html);
    onChange(ref.current.innerHTML);
  };

  const toggleSourceMode = () => {
    if (!sourceMode) {
      // Switching TO source mode — nothing extra needed, value is already synced
    } else {
      // Switching FROM source mode back to visual — apply the raw HTML to the editor
      if (ref.current) {
        ref.current.innerHTML = value;
      }
    }
    setSourceMode(s => !s);
  };

  // ── Table cursor tracking ───────────────────────────────────────────────
  const detectTableCursor = useCallback(() => {
    if (!ref.current || sourceMode) { setActiveTable(null); setActiveCell(null); setTableToolbarRect(null); return; }
    const sel = window.getSelection();
    if (!sel.rangeCount) { setActiveTable(null); setActiveCell(null); setTableToolbarRect(null); return; }
    let node = sel.anchorNode;
    // Text nodes don't have closest(), get parent element first
    if (node && node.nodeType === 3) node = node.parentElement;
    if (!node || !ref.current.contains(node)) { setActiveTable(null); setActiveCell(null); setTableToolbarRect(null); return; }
    const cell = node.closest('td, th');
    const table = node.closest('table');
    setActiveTable(table);
    setActiveCell(cell);
    setTableToolbarRect(cell ? cell.getBoundingClientRect() : null);
  }, [sourceMode]);

  useEffect(() => {
    document.addEventListener('selectionchange', detectTableCursor);
    return () => document.removeEventListener('selectionchange', detectTableCursor);
  }, [detectTableCursor]);

  // ── Table manipulation ──────────────────────────────────────────────────
  const tableAddRow = () => {
    if (!activeTable || !activeCell) return;
    const row = activeCell.closest('tr');
    if (!row) return;
    const newRow = document.createElement('tr');
    for (let i = 0; i < row.cells.length; i++) {
      const td = document.createElement('td');
      td.style.cssText = 'border:1px solid #ddd;padding:10px 14px;';
      td.innerHTML = '&nbsp;';
      newRow.appendChild(td);
    }
    row.after(newRow);
    onChange(ref.current.innerHTML);
  };

  const tableRemoveRow = () => {
    if (!activeTable || !activeCell) return;
    const row = activeCell.closest('tr');
    if (!row || activeTable.querySelectorAll('tr').length <= 1) return;
    row.remove();
    onChange(ref.current.innerHTML);
    setActiveTable(null); setActiveCell(null); setTableToolbarRect(null);
  };

  const tableAddCol = () => {
    if (!activeTable || !activeCell) return;
    const colIdx = activeCell.cellIndex;
    activeTable.querySelectorAll('tr').forEach(row => {
      const refCl = row.cells[colIdx];
      const isHead = refCl?.tagName === 'TH';
      const cell = document.createElement(isHead ? 'th' : 'td');
      cell.style.cssText = isHead
        ? 'border:1px solid #ddd;padding:10px 14px;background:#e67e22;color:white;font-weight:700;text-align:left;'
        : 'border:1px solid #ddd;padding:10px 14px;';
      cell.innerHTML = '&nbsp;';
      refCl ? refCl.after(cell) : row.appendChild(cell);
    });
    onChange(ref.current.innerHTML);
  };

  const tableRemoveCol = () => {
    if (!activeTable || !activeCell) return;
    const colIdx = activeCell.cellIndex;
    const rows = activeTable.querySelectorAll('tr');
    if (rows[0]?.cells.length <= 1) return;
    rows.forEach(row => { if (row.cells[colIdx]) row.cells[colIdx].remove(); });
    onChange(ref.current.innerHTML);
    setActiveTable(null); setActiveCell(null); setTableToolbarRect(null);
  };

  const tools = [
    { label: 'B', title: 'ठळक', style: { fontWeight: 'bold' }, action: () => exec('bold') },
    { label: 'I', title: 'तिरपा', style: { fontStyle: 'italic' }, action: () => exec('italic') },
    { label: 'U', title: 'अधोरेखित', style: { textDecoration: 'underline' }, action: () => exec('underline') },
    { label: 'S', title: 'छेदरेखा', style: { textDecoration: 'line-through' }, action: () => exec('strikeThrough') },
    { sep: true },
    { label: 'H1', title: 'शीर्षक १', style: { fontWeight: 'bold' }, action: () => exec('formatBlock', 'h2') },
    { label: 'H2', title: 'शीर्षक २', style: { fontWeight: 'bold' }, action: () => exec('formatBlock', 'h3') },
    { label: 'P', title: 'परिच्छेद', action: () => exec('formatBlock', 'p') },
    { sep: true },
    { label: '• सूची', title: 'बुलेट सूची', action: () => exec('insertUnorderedList') },
    { label: '1. सूची', title: 'क्रमांकित सूची', action: () => exec('insertOrderedList') },
    { label: '← बाहेर', title: 'बाहेर', action: () => exec('outdent') },
    { label: '→ आत', title: 'आत', action: () => exec('indent') },
    { sep: true },
    { label: '✕', title: 'स्वरूपण काढा', action: () => exec('removeFormat') },
  ];

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '6px 8px', background: '#f7f7f7', borderBottom: '1px solid #ddd', alignItems: 'center' }}>
        {!sourceMode && tools.map((t, i) =>
          t.sep ? <span key={i} style={{ width: 1, height: 20, background: '#ccc', margin: '0 3px' }} /> : (
            <button key={t.label} type="button" title={t.title}
              style={{ padding: '3px 7px', fontSize: '0.75rem', background: 'white', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', ...(t.style || {}) }}
              onMouseDown={e => { e.preventDefault(); t.action(); }}>
              {t.label}
            </button>
          )
        )}
        {!sourceMode && (
          <input type="color" title="Text Color" defaultValue="#333333"
            style={{ width: 26, height: 24, padding: 1, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
            onChange={e => exec('foreColor', e.target.value)} />
        )}
        {!sourceMode && (
          <>
            <span style={{ width: 1, height: 20, background: '#ccc', margin: '0 3px' }} />
            <button type="button" title="प्रतिमा अपलोड करा" disabled={uploading}
              style={{ padding: '3px 7px', fontSize: '0.75rem', background: uploading ? '#eee' : '#e8f4fd', border: '1px solid #a8d4f0', borderRadius: 4, cursor: uploading ? 'wait' : 'pointer', color: '#1a5276', fontWeight: 600 }}
              onMouseDown={e => { e.preventDefault(); fileInputRef.current?.click(); }}>
              {uploading ? '⏳ अपलोड...' : '📷 प्रतिमा'}
            </button>
            <button type="button" title="प्रतिमा URL टाका"
              style={{ padding: '3px 7px', fontSize: '0.75rem', background: 'white', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
              onMouseDown={e => { e.preventDefault(); handleInsertImageUrl(); }}>
              🔗 URL
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            <span style={{ width: 1, height: 20, background: '#ccc', margin: '0 3px' }} />
            <button type="button" title="तक्ता टाका"
              style={{ padding: '3px 7px', fontSize: '0.75rem', background: '#fff8e1', border: '1px solid #e6c84c', borderRadius: 4, cursor: 'pointer', color: '#8d6e00', fontWeight: 600 }}
              onMouseDown={e => { e.preventDefault(); setShowTableModal(true); }}>
              ▇ तक्ता
            </button>
          </>
        )}
        <span style={{ flex: 1 }} />
        {showImageUrlModal && <ImageUrlModal onSubmit={insertImageAtCursor} onClose={() => setShowImageUrlModal(false)} />}
        {showTableModal && <TableModal onSubmit={insertTable} onClose={() => setShowTableModal(false)} />}
        <button
          type="button"
          title={sourceMode ? 'व्हिज्युअल संपादकावर जा' : 'HTML स्रोत संपादित करा'}
          style={{
            padding: '3px 9px', fontSize: '0.75rem', cursor: 'pointer', borderRadius: 4, border: '1px solid #ccc',
            background: sourceMode ? '#1a5276' : 'white',
            color: sourceMode ? 'white' : '#333',
            fontFamily: 'monospace', fontWeight: 700,
          }}
          onClick={toggleSourceMode}
        >&lt;/&gt; {sourceMode ? 'व्हिज्युअल' : 'HTML'}</button>
      </div>

      {sourceMode ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          spellCheck={false}
          style={{
            display: 'block', width: '100%', minHeight: 220, padding: '10px 14px',
            fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.6,
            border: 'none', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
            background: '#1e1e2e', color: '#cdd6f4',
          }}
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          style={{ minHeight: 150, maxHeight: 380, overflowY: 'auto', padding: '10px 14px', outline: 'none', fontSize: '0.9rem', lineHeight: 1.7, fontFamily: 'inherit' }}
          onInput={() => onChange(ref.current.innerHTML)}
        />
      )}

      {/* Floating table toolbar */}
      {activeTable && tableToolbarRect && !sourceMode && (
        <div
          onMouseDown={e => e.preventDefault()}
          style={{
            position: 'fixed', top: tableToolbarRect.top - 34, left: tableToolbarRect.left,
            zIndex: 9999, display: 'flex', gap: 4, alignItems: 'center',
            background: 'white', border: '1.5px solid #e67e22', borderRadius: 8,
            padding: '4px 10px', boxShadow: '0 3px 14px rgba(0,0,0,0.13)', fontSize: '0.73rem',
          }}
        >
          <span style={{ fontWeight: 700, color: '#e67e22', marginRight: 4 }}>तक्ता</span>
          <button type="button" onClick={tableAddRow} title="ओळ जोडा (खाली)"
            style={{ padding: '2px 7px', background: '#e8f8e8', border: '1px solid #7bc67b', borderRadius: 5, cursor: 'pointer', fontWeight: 600, color: '#2e7d32' }}>+ ओळ</button>
          <button type="button" onClick={tableRemoveRow} title="ही ओळ काढा"
            style={{ padding: '2px 7px', background: '#fde8e8', border: '1px solid #e57373', borderRadius: 5, cursor: 'pointer', fontWeight: 600, color: '#c62828' }}>− ओळ</button>
          <span style={{ width: 1, height: 16, background: '#ddd', margin: '0 2px' }} />
          <button type="button" onClick={tableAddCol} title="स्तंभ जोडा (उजवीकडे)"
            style={{ padding: '2px 7px', background: '#e8f8e8', border: '1px solid #7bc67b', borderRadius: 5, cursor: 'pointer', fontWeight: 600, color: '#2e7d32' }}>+ स्तंभ</button>
          <button type="button" onClick={tableRemoveCol} title="हा स्तंभ काढा"
            style={{ padding: '2px 7px', background: '#fde8e8', border: '1px solid #e57373', borderRadius: 5, cursor: 'pointer', fontWeight: 600, color: '#c62828' }}>− स्तंभ</button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────
export default function Content() {
  const { tenant, setTenant } = useAuth();
  const slug = tenant?.slug;

  const [pages, setPages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('settings');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [websiteTheme, setWebsiteTheme] = useState({});
  const [pageHeroImages, setPageHeroImages] = useState({});
  const [savingHeroImages, setSavingHeroImages] = useState(false);
  const [heroUploadingKey, setHeroUploadingKey] = useState(null);
  const [activeUploadPageKey, setActiveUploadPageKey] = useState(null);
  const heroFileInputRef = useRef(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [repUploading, setRepUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const bannerFileInputRef = useRef(null);
  const repFileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const [tenantRecordId, setTenantRecordId] = useState(tenant?.id || null);

  // GP Settings — pre-filled from fresh API call
  const [settings, setSettings] = useState({
    about_text: '', vision_text: '', sarpanch_name: '',
    contact_phone: '', contact_email: '', official_website_url: '', address: '',
    office_hours: '', logo_url: '', banner_url: '', latitude: '', longitude: '',
    facebook_url: '', instagram_url: '', youtube_url: '', twitter_url: '',
    population: '', total_households: '', taluka: '',
    district: '', state: '', pincode: '',
    village_established_year: '', established_year: '',
    area: '', road_length: '', rainfall: '', main_occupation: '',
    literacy_rate: '', sex_ratio: '', village_deity: '',
  });

  // Banners (multi-banner stored as a page)
  const [banners, setBanners] = useState([]);
  const [bannersPageId, setBannersPageId] = useState(null);
  const [bannerForm, setBannerForm] = useState({ url: '', title: '', caption: '' });
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [editingBannerIdx, setEditingBannerIdx] = useState(null);

  // Pages
  const BLANK_PAGE = { title: '', slug: '', is_published: true, page_type: 'custom' };
  const [pageForm, setPageForm] = useState(BLANK_PAGE);
  const [editingPage, setEditingPage] = useState(null);
  const [showPageForm, setShowPageForm] = useState(false);
  const [pageSections, setPageSections] = useState([{ id: Date.now(), heading: '', content: '', order: 0 }]);

  // Fees
  const [editingFee, setEditingFee] = useState(null);
  const [showFeeForm, setShowFeeForm] = useState(false);
  const [newFeeForm, setNewFeeForm] = useState({ service_type: '', service_name: '', fee_amount: '', contact_gp_for_fees: false });

  // Representatives
  const [reps, setReps] = useState([]);
  const [repsPageId, setRepsPageId] = useState(null);
  const [repForm, setRepForm] = useState(BLANK_REP);
  const [editRepIdx, setEditRepIdx] = useState(null);
  const [showRepForm, setShowRepForm] = useState(false);

  // About Page Dynamic Sections
  const [aboutSections, setAboutSections] = useState([]);

  // Fetch fresh tenant data from API instead of relying on auth context (which only has id/name/slug)
  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [pRes, gRes, fRes, tRes] = await Promise.all([
        contentAPI.getPagesAdmin(slug).catch(() => ({ data: { pages: [] } })),
        contentAPI.getGalleryAdmin(slug).catch(() => ({ data: { images: [] } })),
        contentAPI.getFees(slug).catch(() => ({ data: { fees: [] } })),
        tenantAPI.getBySlug(slug).catch(() => ({ data: { tenant: null } })),
      ]);

      const allPages = pRes.data.pages || [];
      const freshTenant = tRes.data.tenant;
      setPages(allPages);
      setGallery(gRes.data.images || []);
      setFees(fRes.data.fees || []);

      // Banners page
      const bPage = allPages.find(p => p.slug === 'banners');
      setBannersPageId(bPage?.id || null);
      try { setBanners(bPage ? JSON.parse(bPage.content || '[]') : []); } catch { setBanners([]); }

      // Representatives page
      const rPage = allPages.find(p => p.slug === 'representatives');
      setRepsPageId(rPage?.id || null);
      try { setReps(rPage ? JSON.parse(rPage.content || '[]') : []); } catch { setReps([]); }

      // Pre-fill settings from FRESH tenant data
      if (freshTenant) {
        setTenantRecordId(freshTenant.id);
        setWebsiteTheme(freshTenant.website_theme || {});
        setPageHeroImages(freshTenant.website_theme?.page_hero_images || {});
        setSettings({
          about_text: freshTenant.about_text || '',
          vision_text: freshTenant.vision_text || '',
          sarpanch_name: freshTenant.sarpanch_name || '',
          contact_phone: freshTenant.contact_phone || '',
          contact_email: freshTenant.contact_email || '',
          official_website_url: freshTenant.website_theme?.official_website_url
            || (freshTenant.slug === 'deulgaon-gada' ? 'https://deulgaongada.com/' : ''),
          address: freshTenant.address || '',
          office_hours: freshTenant.office_hours || '',
          logo_url: freshTenant.logo_url || '',
          banner_url: freshTenant.banner_url || '',
          latitude: freshTenant.latitude || '',
          longitude: freshTenant.longitude || '',
          facebook_url: freshTenant.website_theme?.social_links?.facebook || '',
          instagram_url: freshTenant.website_theme?.social_links?.instagram || '',
          youtube_url: freshTenant.website_theme?.social_links?.youtube || '',
          twitter_url: freshTenant.website_theme?.social_links?.twitter || '',
          population: freshTenant.population || '',
          total_households: freshTenant.total_households || '',
          taluka: freshTenant.taluka || '',
          district: freshTenant.district || '',
          state: freshTenant.state || '',
          pincode: freshTenant.pincode || '',
          village_established_year: freshTenant.village_established_year || '',
          established_year: freshTenant.established_year || '',
          area: freshTenant.area || '',
          road_length: freshTenant.road_length || '',
          rainfall: freshTenant.rainfall || '',
          main_occupation: freshTenant.main_occupation || '',
          literacy_rate: freshTenant.literacy_rate || '',
          sex_ratio: freshTenant.sex_ratio || '',
          village_deity: freshTenant.village_deity || '',
        });

        // About sections: use about_sections if available, else migrate from about_text/vision_text
        if (freshTenant.about_sections && Array.isArray(freshTenant.about_sections) && freshTenant.about_sections.length > 0) {
          setAboutSections(freshTenant.about_sections);
        } else {
          const migrated = [];
          if (freshTenant.about_text) migrated.push({ id: Date.now(), heading: 'आमच्या ग्रामपंचायतबद्दल', content: freshTenant.about_text, order: 0 });
          if (freshTenant.vision_text) migrated.push({ id: Date.now() + 1, heading: 'आमची दृष्टी', content: freshTenant.vision_text, order: 1 });
          setAboutSections(migrated);
        }
      }
    } catch (err) { setError('डेटा लोड करण्यात अयशस्वी'); }
    finally { setLoading(false); }
  }, [slug]); // intentionally excludes 'tenant' to avoid re-fetch loop after save

  useEffect(() => { loadData(); }, [loadData]);

  const flash = (msg, isErr = false) => {
    if (isErr) { setError(msg); setSuccess(''); } else { setSuccess(msg); setError(''); }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const validateImageFile = (file) => {
    if (!file) return 'कृपया प्रतिमा निवडा.';
    if (!file.type.startsWith('image/')) return 'केवळ प्रतिमा फायली स्वीकारल्या जातात.';
    if (file.size > IMAGE_MAX_SIZE) return 'प्रतिमा 2MB पेक्षा लहान असावी.';
    return null;
  };

  const uploadImageFile = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    if (!slug) throw new Error('Tenant not loaded');
    const formData = new FormData();
    formData.append('image', file);
    const response = await contentAPI.uploadImage(slug, formData);
    return response.data?.url;
  };

  const handleBannerImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setBannerUploading(true);
    try {
      const url = await uploadImageFile(file);
      setBannerForm(prev => ({ ...prev, url: url || prev.url }));
      showToast('Banner image uploaded successfully.', 'success');
    } catch (err) {
      showToast(err.message || 'Banner upload failed.', 'error');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleRepImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setRepUploading(true);
    try {
      const url = await uploadImageFile(file);
      setRepForm(prev => ({ ...prev, photo: url || prev.photo }));
      showToast('Representative photo uploaded successfully.', 'success');
    } catch (err) {
      showToast(err.message || 'Photo upload failed.', 'error');
    } finally {
      setRepUploading(false);
    }
  };

  const handleLogoImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLogoUploading(true);
    try {
      const url = await uploadImageFile(file);
      setSettings(prev => ({ ...prev, logo_url: url || prev.logo_url }));
      showToast('Logo uploaded successfully.', 'success');
    } catch (err) {
      showToast(err.message || 'Logo upload failed.', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  // ── Settings ──────────────────────────────────────────────────────────
  const saveSettings = async (e) => {
    e.preventDefault();
    const hasLatitude = settings.latitude !== '' && settings.latitude !== null;
    const hasLongitude = settings.longitude !== '' && settings.longitude !== null;
    if (hasLatitude !== hasLongitude || ((hasLatitude || hasLongitude) && !normalizeLocation(settings.latitude, settings.longitude))) {
      showToast('कार्यालयाचे अक्षांश आणि रेखांश दोन्ही वैध असणे आवश्यक आहे.', 'error');
      return;
    }
    if (!tenantRecordId) {
      showToast('ग्रामपंचायत नोंद ओळखता आली नाही. पृष्ठ पुन्हा लोड करून प्रयत्न करा.', 'error');
      return;
    }
    setSaving(true);
    try {
      const {
        facebook_url, instagram_url, youtube_url, twitter_url, official_website_url, ...tenantSettings
      } = settings;
      const socialLinks = {
        facebook: facebook_url.trim(),
        instagram: instagram_url.trim(),
        youtube: youtube_url.trim(),
        twitter: twitter_url.trim(),
      };
      const payload = {
        ...tenantSettings,
        latitude: hasLatitude ? Number(tenantSettings.latitude) : null,
        longitude: hasLongitude ? Number(tenantSettings.longitude) : null,
        about_sections: aboutSections,
        website_theme: {
          ...websiteTheme,
          page_hero_images: pageHeroImages,
          official_website_url: official_website_url.trim(),
          social_links: socialLinks,
        },
      };
      const response = await tenantAPI.updateContent(tenantRecordId, payload);
      const savedTenant = response.data?.tenant || payload;
      setWebsiteTheme(savedTenant.website_theme || payload.website_theme);
      setSettings(current => ({
        ...current,
        latitude: savedTenant.latitude ?? '',
        longitude: savedTenant.longitude ?? '',
      }));
      setTenant({ ...tenant, ...savedTenant });
      showToast('सेटिंग्ज यशस्वीरित्या जतन केल्या!', 'success');
    } catch { showToast('सेटिंग्ज जतन करण्यात अयशस्वी', 'error'); }
    finally { setSaving(false); }
  };

  // ── About sections management ─────────────────────────────────────────
  const addAboutSection = () => {
    setAboutSections(prev => [...prev, { id: Date.now(), heading: '', content: '', order: prev.length }]);
  };
  const deleteAboutSection = (id) => {
    if (!window.confirm('हा विभाग हटवायचा?')) return;
    setAboutSections(prev => prev.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i })));
  };
  const moveAboutSection = (idx, dir) => {
    setAboutSections(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr.map((s, i) => ({ ...s, order: i }));
    });
  };
  const updateAboutSection = (id, field, value) => {
    setAboutSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  // ── Banners ───────────────────────────────────────────────────────────
  const saveBanners = async (updated) => {
    try {
      const content = JSON.stringify(updated);
      if (bannersPageId) {
        await contentAPI.updatePage(slug, bannersPageId, { content, is_published: true });
      } else {
        const res = await contentAPI.createPage(slug, {
          title: 'Banners', slug: 'banners', page_type: 'custom', content, is_published: true,
        });
        setBannersPageId(res.data.page?.id);
      }
      setBanners(updated);
      flash('बॅनर अद्ययावत केले!');
    } catch { flash('बॅनर जतन करण्यात अयशस्वी', true); }
  };

  const addBanner = (e) => {
    e.preventDefault();
    if (!bannerForm.url) return;
    saveBanners([...banners, { ...bannerForm, id: Date.now() }]);
    setBannerForm({ url: '', title: '', caption: '' });
    setEditingBannerIdx(null);
  };

  const deleteBanner = (idx) => {
    if (!window.confirm('हा बॅनर काढायचा?')) return;
    setEditingBannerIdx(null);
    saveBanners(banners.filter((_, i) => i !== idx));
  };

  // ── Page Hero Images ──────────────────────────────────────────────────
  const handleHeroImageFile = async (e) => {
    const file = e.target.files?.[0];
    const pageKey = activeUploadPageKey;
    if (!file || !pageKey) return;
    e.target.value = '';
    setHeroUploadingKey(pageKey);
    try {
      const url = await uploadImageFile(file);
      if (url) {
        const next = { ...pageHeroImages, [pageKey]: url };
        setPageHeroImages(next);
        await savePageHeroImages(next);
        showToast('पृष्ठ पार्श्वभूमी प्रतिमा अपलोड व जतन झाली!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'प्रतिमा अपलोड अयशस्वी.', 'error');
    } finally {
      setHeroUploadingKey(null);
      setActiveUploadPageKey(null);
    }
  };

  const updateHeroImageUrl = (pageKey, url) => {
    setPageHeroImages(prev => ({ ...prev, [pageKey]: url }));
  };

  const resetHeroImage = async (pageKey) => {
    const next = { ...pageHeroImages };
    delete next[pageKey];
    setPageHeroImages(next);
    await savePageHeroImages(next);
    showToast('डीफॉल्ट प्रतिमेवर रीसेट केले!', 'success');
  };

  const savePageHeroImages = async (newImages = pageHeroImages) => {
    if (!tenantRecordId) {
      showToast('ग्रामपंचायत नोंद ओळखता आली नाही.', 'error');
      return;
    }
    setSavingHeroImages(true);
    try {
      const updatedTheme = {
        ...websiteTheme,
        page_hero_images: newImages,
      };
      const payload = {
        website_theme: updatedTheme,
      };
      const response = await tenantAPI.updateContent(tenantRecordId, payload);
      const savedTenant = response.data?.tenant;
      if (savedTenant) {
        setWebsiteTheme(savedTenant.website_theme || updatedTheme);
        setTenant(prev => ({ ...prev, ...savedTenant }));
      }
      setPageHeroImages(newImages);
      showToast('पृष्ठ पार्श्वभूमी चित्रे यशस्वीरित्या जतन केली!', 'success');
    } catch {
      showToast('पार्श्वभूमी चित्रे जतन करण्यात अयशस्वी', 'error');
    } finally {
      setSavingHeroImages(false);
    }
  };

  // ── Pages ─────────────────────────────────────────────────────────────
  const savePage = async (e) => {
    e.preventDefault();
    try {
      const payload = { title: pageForm.title, is_published: pageForm.is_published, sections: pageSections };
      if (editingPage) {
        await contentAPI.updatePage(slug, editingPage.id, payload);
        flash('पृष्ठ अद्ययावत केले!');
      } else {
        await contentAPI.createPage(slug, { ...pageForm, ...payload, page_type: 'custom' });
        flash('पृष्ठ तयार केले!');
      }
      setShowPageForm(false); setEditingPage(null); setPageForm(BLANK_PAGE);
      setPageSections([{ id: Date.now(), heading: '', content: '', order: 0 }]);
      loadData();
    } catch (err) { flash(err.response?.data?.error || 'पृष्ठ जतन करण्यात अयशस्वी', true); }
  };

  const deletePage = async (id) => {
    if (!window.confirm('हे पृष्ठ हटवायचे?')) return;
    try { await contentAPI.deletePage(slug, id); flash('पृष्ठ हटवले'); loadData(); }
    catch { flash('हटवण्यात अयशस्वी', true); }
  };

  const togglePage = async (p) => {
    try { await contentAPI.updatePage(slug, p.id, { is_published: !p.is_published }); flash('पृष्ठ अद्ययावत केले'); loadData(); }
    catch { flash('अयशस्वी', true); }
  };

  const startEditPage = (p) => {
    setPageForm({ title: p.title, slug: p.slug, is_published: p.is_published, page_type: p.page_type || 'custom' });
    // Load sections: use page.sections if available, else migrate from page.content
    if (p.sections && Array.isArray(p.sections) && p.sections.length > 0) {
      setPageSections(p.sections);
    } else if (p.content) {
      setPageSections([{ id: Date.now(), heading: 'मुख्य माहिती', content: p.content, order: 0 }]);
    } else {
      setPageSections([{ id: Date.now(), heading: '', content: '', order: 0 }]);
    }
    setEditingPage(p); setShowPageForm(true);
  };

  // ── Page sections management ──────────────────────────────────────────
  const addPageSection = () => {
    setPageSections(prev => [...prev, { id: Date.now(), heading: '', content: '', order: prev.length }]);
  };
  const deletePageSection = (id) => {
    if (pageSections.length <= 1) return;
    if (!window.confirm('हा विभाग हटवायचा?')) return;
    setPageSections(prev => prev.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i })));
  };
  const movePageSection = (idx, dir) => {
    setPageSections(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr.map((s, i) => ({ ...s, order: i }));
    });
  };
  const updatePageSection = (id, field, value) => {
    setPageSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  // ── Fees ──────────────────────────────────────────────────────────────
  const startEditFee = (fee) => {
    const ex = (typeof fee.exemptions === 'string' ? (() => { try { return JSON.parse(fee.exemptions); } catch { return {}; } })() : fee.exemptions) || {};
    setEditingFee({
      id: fee.id,
      service_type: fee.service_type,
      service_name: fee.service_name,
      contact_gp_for_fees: Boolean(fee.contact_gp_for_fees),
      fee_amount: fee.fee_amount,
      duplicate_fee: fee.duplicate_fee || '',
      exemptions: EXEMPTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: ex[k] || '' }), {}),
    });
  };

  const saveEditingFee = async (e) => {
    e.preventDefault();
    const cleanEx = EXEMPTION_KEYS.reduce((acc, k) => {
      if (editingFee.exemptions[k]) acc[k] = Number(editingFee.exemptions[k]);
      return acc;
    }, {});
    try {
      await contentAPI.upsertFee(slug, {
        service_type: editingFee.service_type,
        service_name: editingFee.service_name,
        contact_gp_for_fees: editingFee.contact_gp_for_fees,
        fee_amount: editingFee.contact_gp_for_fees ? 0 : editingFee.fee_amount,
        duplicate_fee: editingFee.contact_gp_for_fees ? null : (editingFee.duplicate_fee || null),
        exemptions: editingFee.contact_gp_for_fees ? null : (Object.keys(cleanEx).length ? cleanEx : null),
        is_active: true,
      });
      flash('शुल्क अद्ययावत केले!'); setEditingFee(null); loadData();
    } catch { flash('शुल्क अद्ययावत करण्यात अयशस्वी', true); }
  };

  const addNewFee = async (e) => {
    e.preventDefault();
    try {
      await contentAPI.upsertFee(slug, {
        service_type: newFeeForm.service_type.toLowerCase().replace(/\s+/g, '_'),
        service_name: newFeeForm.service_name,
        contact_gp_for_fees: newFeeForm.contact_gp_for_fees,
        fee_amount: newFeeForm.contact_gp_for_fees ? 0 : newFeeForm.fee_amount,
        is_active: true,
      });
      flash('शुल्क जोडले!');
      setNewFeeForm({ service_type: '', service_name: '', fee_amount: '', contact_gp_for_fees: false });
      setShowFeeForm(false); loadData();
    } catch { flash('शुल्क जोडण्यात अयशस्वी', true); }
  };

  // ── Representatives ───────────────────────────────────────────────────
  const saveReps = async (updated) => {
    try {
      const content = JSON.stringify(updated);
      if (repsPageId) {
        await contentAPI.updatePage(slug, repsPageId, { content, is_published: true });
      } else {
        const res = await contentAPI.createPage(slug, {
          title: 'Representatives', slug: 'representatives', page_type: 'custom', content, is_published: true,
        });
        setRepsPageId(res.data.page?.id);
      }
      setReps(updated); flash('लोकप्रतिनिधी अद्ययावत केले!');
    } catch { flash('लोकप्रतिनिधी जतन करण्यात अयशस्वी', true); }
  };

  const handleRepSubmit = async (e) => {
    e.preventDefault();
    const updated = [...reps];
    if (editRepIdx !== null) updated[editRepIdx] = repForm;
    else updated.push({ ...repForm, id: Date.now() });
    await saveReps(updated);
    setRepForm(BLANK_REP); setEditRepIdx(null); setShowRepForm(false);
  };

  const deleteRep = (idx) => {
    if (!window.confirm('हा लोकप्रतिनिधी हटवायचा?')) return;
    saveReps(reps.filter((_, i) => i !== idx));
  };

  const startEditRep = (rep, idx) => {
    setRepForm({ name: rep.name, designation: rep.designation, photo: rep.photo || '', category: rep.category || 'other' });
    setEditRepIdx(idx); setShowRepForm(true);
  };

  const moveRep = (idx, dir) => {
    const updated = [...reps];
    const t = idx + dir;
    if (t < 0 || t >= updated.length) return;
    [updated[idx], updated[t]] = [updated[t], updated[idx]];
    saveReps(updated);
  };

  const TABS = [
    { key: 'settings', label: '⚙️ ग्रा.पं. सेटिंग्ज' },
    { key: 'representatives', label: '👤 लोकप्रतिनिधी' },
    { key: 'pages', label: '📄 पृष्ठे' },
    { key: 'gallery', label: '🖼️ गॅलरी' },
    { key: 'fees', label: '💰 शुल्क' },
    { key: 'messages', label: '✉️ नागरिक संदेश' },
  ];

  const visiblePages = pages.filter(p => !['representatives', 'banners'].includes(p.slug));

  const cardStyle = { background: '#f0f4ff', border: '1px solid #c8d8f5', borderRadius: 10, padding: 16, marginBottom: 16 };

  return (
    <div>
      <div className="admin-card-header">
        <h2>🌐 वेबसाइट सामग्री</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TABS.map(t => (
            <button key={t.key} className={`btn ${tab === t.key ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {success && <div className="alert alert-success" style={{ margin: '10px 0' }}>{success}</div>}
      {error && <div className="alert alert-error" style={{ margin: '10px 0' }}>{error}</div>}

      <div className="admin-card">
        {loading ? <p>लोड होत आहे...</p> : (
          <>
            {/* ── SETTINGS ─────────────────────────────────────────────── */}
            {tab === 'settings' && (
              <form onSubmit={saveSettings}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                  <h3 style={{ margin: 0, color: '#1a5276' }}>ग्रामपंचायत माहिती</h3>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                    style={{ opacity: saving ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    {saving ? (
                      <><span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> जतन होत आहे...</>
                    ) : (
                      <>💾 GP सेटिंग्ज जतन करा</>
                    )}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                  <div className="form-group">
                    <label>सरपंचाचे नाव</label>
                    <input value={settings.sarpanch_name} onChange={e => setSettings({ ...settings, sarpanch_name: e.target.value })} placeholder="उदा. श्री. राजवर्धन जगताप" />
                  </div>
                  <div className="form-group">
                    <label>फोन</label>
                    <input value={settings.contact_phone} onChange={e => setSettings({ ...settings, contact_phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>ईमेल</label>
                    <input type="email" value={settings.contact_email} onChange={e => setSettings({ ...settings, contact_email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>अधिकृत वेबसाइट</label>
                    <input
                      type="url"
                      value={settings.official_website_url}
                      onChange={e => setSettings({ ...settings, official_website_url: e.target.value })}
                      placeholder="https://deulgaongada.com/"
                    />
                  </div>
                  <div className="form-group">
                    <label>कार्यालय वेळ</label>
                    <input value={settings.office_hours} onChange={e => setSettings({ ...settings, office_hours: e.target.value })} placeholder="उदा. सकाळी ९:00 - संध्याकाळी ५:00 (सोम-शनि)" />
                  </div>
                  <div className="form-group">
                    <label>लोकसंख्या</label>
                    <input type="number" value={settings.population} onChange={e => setSettings({ ...settings, population: e.target.value })} placeholder="उदा. ८५००" />
                  </div>
                  <div className="form-group">
                    <label>एकूण कुटुंबे</label>
                    <input type="number" value={settings.total_households} onChange={e => setSettings({ ...settings, total_households: e.target.value })} placeholder="उदा. १८००" />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>पत्ता</label>
                    <input value={settings.address} onChange={e => setSettings({ ...settings, address: e.target.value })} />
                  </div>
                  <LocationPicker
                    id="gp-office-location"
                    latitude={settings.latitude}
                    longitude={settings.longitude}
                    onChange={coordinates => setSettings(current => ({ ...current, ...coordinates }))}
                    label="ग्रामपंचायत कार्यालयाचे नकाशावरील स्थान"
                    help="नकाशावर कार्यालयाचे अचूक स्थान निवडा किंवा सध्याचे स्थान वापरा. हेच स्थान संकेतस्थळाच्या तळाशी नागरिकांना दिसेल."
                  />
                  <div className="form-group">
                    <label>जिल्हा</label>
                    <input value={settings.district} onChange={e => setSettings({ ...settings, district: e.target.value })} placeholder="उदा. पुणे" />
                  </div>
                  <div className="form-group">
                    <label>तालुका</label>
                    <input value={settings.taluka} onChange={e => setSettings({ ...settings, taluka: e.target.value })} placeholder="उदा. बारामती" />
                  </div>
                  <div className="form-group">
                    <label>राज्य</label>
                    <input value={settings.state} onChange={e => setSettings({ ...settings, state: e.target.value })} placeholder="उदा. महाराष्ट्र" />
                  </div>
                  <div className="form-group">
                    <label>पिन कोड</label>
                    <input value={settings.pincode} onChange={e => setSettings({ ...settings, pincode: e.target.value })} placeholder="उदा. ५१३१०२" />
                  </div>
                  <div className="form-group">
                    <label>गावाची स्थापना</label>
                    <input value={settings.village_established_year} onChange={e => setSettings({ ...settings, village_established_year: e.target.value })} placeholder="उदा. १९७५" />
                  </div>
                  <div className="form-group">
                    <label>ग्रामपंचायत स्थापना</label>
                    <input value={settings.established_year} onChange={e => setSettings({ ...settings, established_year: e.target.value })} placeholder="उदा. १९८२" />
                  </div>
                  <div className="form-group">
                    <label>क्षेत्रफळ</label>
                    <input value={settings.area} onChange={e => setSettings({ ...settings, area: e.target.value })} placeholder="उदा. १२५० हे." />
                  </div>
                  <div className="form-group">
                    <label>रस्ते लांबी</label>
                    <input value={settings.road_length} onChange={e => setSettings({ ...settings, road_length: e.target.value })} placeholder="उदा. १८ कि.मी." />
                  </div>
                  <div className="form-group">
                    <label>पर्जन्यमान</label>
                    <input value={settings.rainfall} onChange={e => setSettings({ ...settings, rainfall: e.target.value })} placeholder="उदा. ६५० मि.मी." />
                  </div>
                  <div className="form-group">
                    <label>मुख्य व्यवसाय</label>
                    <input value={settings.main_occupation} onChange={e => setSettings({ ...settings, main_occupation: e.target.value })} placeholder="उदा. शेती" />
                  </div>
                  <div className="form-group">
                    <label>साक्षरता दर</label>
                    <input value={settings.literacy_rate} onChange={e => setSettings({ ...settings, literacy_rate: e.target.value })} placeholder="उदा. ७८.४२%" />
                  </div>
                  <div className="form-group">
                    <label>स्त्री-पुरुष गुणोत्तर</label>
                    <input value={settings.sex_ratio} onChange={e => setSettings({ ...settings, sex_ratio: e.target.value })} placeholder="उदा. ९३०" />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>ग्रामदैवत</label>
                    <input value={settings.village_deity} onChange={e => setSettings({ ...settings, village_deity: e.target.value })} placeholder="उदा. श्री भैरवनाथ" />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label>लोगो URL</label>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <input
                        value={settings.logo_url}
                        onChange={e => setSettings({ ...settings, logo_url: e.target.value })}
                        placeholder="https://..."
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => logoFileInputRef.current?.click()}
                        disabled={logoUploading}
                        style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                      >{logoUploading ? 'Uploading...' : 'Upload'}</button>
                      <input ref={logoFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoImageFile} />
                      {settings.logo_url ? (
                        <img
                          key={settings.logo_url}
                          src={settings.logo_url}
                          alt="logo"
                          style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, border: '1px solid #ddd', background: '#f9f9f9', flexShrink: 0 }}
                          onLoad={e => { e.currentTarget.style.visibility = 'visible'; }}
                          onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <div style={{ width: 56, height: 56, borderRadius: 6, border: '2px dashed #ddd', background: '#f9f9f9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '1.4rem' }}>🏛️</div>
                      )}
                    </div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2', marginTop: 8 }}>
                    <h4 style={{ margin: '0 0 5px', color: '#1a5276' }}>सामाजिक माध्यम दुवे</h4>
                    <small style={{ color: '#667788' }}>फक्त ग्रामपंचायतीची अधिकृत खाती जोडा. रिकामे ठेवलेले दुवे सार्वजनिक footer मध्ये दिसणार नाहीत.</small>
                  </div>
                  <div className="form-group">
                    <label>Facebook URL</label>
                    <input type="url" value={settings.facebook_url} onChange={e => setSettings({ ...settings, facebook_url: e.target.value })} placeholder="https://facebook.com/..." />
                  </div>
                  <div className="form-group">
                    <label>Instagram URL</label>
                    <input type="url" value={settings.instagram_url} onChange={e => setSettings({ ...settings, instagram_url: e.target.value })} placeholder="https://instagram.com/..." />
                  </div>
                  <div className="form-group">
                    <label>YouTube URL</label>
                    <input type="url" value={settings.youtube_url} onChange={e => setSettings({ ...settings, youtube_url: e.target.value })} placeholder="https://youtube.com/@..." />
                  </div>
                  <div className="form-group">
                    <label>X (Twitter) URL</label>
                    <input type="url" value={settings.twitter_url} onChange={e => setSettings({ ...settings, twitter_url: e.target.value })} placeholder="https://x.com/..." />
                  </div>
                </div>

                {/* ── About Page Dynamic Sections ── */}
                <hr style={{ margin: '28px 0 16px', border: 'none', borderTop: '1px solid #e8e8e8' }} />
                <h3 style={{ margin: '0 0 4px', color: '#1a5276' }}>📄 आमच्या बद्दल पृष्ठ विभाग</h3>
                <p style={{ margin: '0 0 14px', color: '#888', fontSize: '0.82rem' }}>
                  आमच्या बद्दल पृष्ठावर दिसणारे विभाग व्यवस्थापित करा. नवीन विभाग जोडा, संपादित करा किंवा हटवा.
                </p>

                {aboutSections.length === 0 && (
                  <p style={{ color: '#aaa', fontStyle: 'italic', margin: '10px 0' }}>कोणतेही विभाग नाहीत. खाली "नवीन विभाग जोडा" वर क्लिक करा.</p>
                )}

                {aboutSections.map((sec, idx) => (
                  <div key={sec.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 14, background: '#fafcff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <span style={{ background: '#1a5276', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                      <input
                        value={sec.heading}
                        onChange={e => updateAboutSection(sec.id, 'heading', e.target.value)}
                        placeholder="विभागाचे शीर्षक प्रविष्ट करा..."
                        style={{ flex: 1, padding: '8px 12px', fontSize: '1rem', fontWeight: 600, border: '1px solid #d0d0d0', borderRadius: 6 }}
                      />
                      <button type="button" onClick={() => moveAboutSection(idx, -1)} disabled={idx === 0} title="वर हलवा" style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>▲</button>
                      <button type="button" onClick={() => moveAboutSection(idx, 1)} disabled={idx === aboutSections.length - 1} title="खाली हलवा" style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px', cursor: idx === aboutSections.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === aboutSections.length - 1 ? 0.4 : 1 }}>▼</button>
                      <button type="button" onClick={() => deleteAboutSection(sec.id)} title="हटवा" style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' }}>🗑️</button>
                    </div>
                    <RichTextEditor
                      value={sec.content}
                      onChange={v => updateAboutSection(sec.id, 'content', v)}
                      placeholder="विभागाची माहिती लिहा..."
                      tenantSlug={slug}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addAboutSection}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#f0f7ff', border: '2px dashed #1a5276', borderRadius: 8, color: '#1a5276', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', margin: '10px 0' }}
                >
                  ➕ नवीन विभाग जोडा
                </button>

                <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 15, opacity: saving ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {saving ? (
                    <><span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> जतन होत आहे...</>
                  ) : (
                    <>💾 ग्रा.पं. सेटिंग्ज जतन करा</>
                  )}
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </button>

                {/* ── Banners Section ── */}
                <hr style={{ margin: '28px 0 16px', border: 'none', borderTop: '1px solid #e8e8e8' }} />
                <h3 style={{ margin: '0 0 4px', color: '#1a5276' }}>🖼️ मुखपृष्ठ बॅनर</h3>
                <p style={{ margin: '0 0 14px', color: '#888', fontSize: '0.82rem' }}>हे मुखपृष्ठावर स्वयं-स्क्रोलिंग स्लाइडशो म्हणून दिसतात. तुम्हाला हवे तितके जोडा.</p>

                {/* Existing banners list */}
                {banners.length > 0 && (
                  <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                    {banners.map((b, i) => (
                      <div key={i}>
                        {editingBannerIdx === i ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, background: '#fafafa', border: '2px solid #1a5276', borderRadius: 8, padding: 12, alignItems: 'start' }}>
                            <img src={b.url} alt="" style={{ width: 120, height: 66, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} onError={e => { e.target.style.opacity = '0.5'; }} />
                            <div style={{ display: 'grid', gap: 8 }}>
                              <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a5276', display: 'block', marginBottom: 4 }}>Title</label>
                                <input
                                  type="text"
                                  value={b.title || ''}
                                  onChange={e => {
                                    const updated = [...banners];
                                    updated[i] = { ...updated[i], title: e.target.value };
                                    setBanners(updated);
                                  }}
                                  placeholder="Banner title"
                                  style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a5276', display: 'block', marginBottom: 4 }}>Caption (optional)</label>
                                <input
                                  type="text"
                                  value={b.caption || ''}
                                  onChange={e => {
                                    const updated = [...banners];
                                    updated[i] = { ...updated[i], caption: e.target.value };
                                    setBanners(updated);
                                  }}
                                  placeholder="Caption text"
                                  style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditingBannerIdx(null)}>Cancel</button>
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => { saveBanners(banners); setEditingBannerIdx(null); }}>Save Changes</button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 12px' }}>
                            <span style={{ fontWeight: 700, color: '#aaa', fontSize: '0.85rem', minWidth: 24 }}>#{i + 1}</span>
                            <img src={b.url} alt="" style={{ width: 120, height: 66, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid #eee' }} onError={e => { e.target.style.opacity = '0.3'; }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title || `Banner ${i + 1}`}</p>
                              {b.caption && <p style={{ margin: '2px 0 0', color: '#888', fontSize: '0.78rem' }}>{b.caption}</p>}
                              <p style={{ margin: '2px 0 0', color: '#bbb', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.url}</p>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditingBannerIdx(i)} title="Edit">✏️</button>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                                const updated = [...banners];
                                if (i > 0) { [updated[i - 1], updated[i]] = [updated[i], updated[i - 1]]; saveBanners(updated); }
                              }} disabled={i === 0} title="Move up">↑</button>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                                const updated = [...banners];
                                if (i < updated.length - 1) { [updated[i], updated[i + 1]] = [updated[i + 1], updated[i]]; saveBanners(updated); }
                              }} disabled={i === banners.length - 1} title="Move down">↓</button>
                              <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteBanner(i)}>✕</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Inline add-banner row */}
                <div style={{ border: '2px dashed #c8d8f5', borderRadius: 10, padding: 14, background: '#f8faff' }}>
                  <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#1a5276', fontSize: '0.88rem' }}>➕ Add New Banner</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.78rem' }}>Image URL *</label>
                      <input value={bannerForm.url} onChange={e => setBannerForm({ ...bannerForm, url: e.target.value })} placeholder="https://example.com/image.jpg" />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => bannerFileInputRef.current?.click()}
                      disabled={bannerUploading}
                      style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                    >{bannerUploading ? 'Uploading...' : 'Upload image'}</button>
                    <input ref={bannerFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBannerImageFile} />
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.78rem' }}>Title (optional)</label>
                      <input value={bannerForm.title} onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="Banner title" style={{ width: 180 }} />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                      onClick={addBanner}
                      disabled={!bannerForm.url}
                    >+Add</button>
                  </div>
                  {bannerForm.url && (
                    <img src={bannerForm.url} alt="preview" style={{ marginTop: 10, height: 70, borderRadius: 6, border: '1px solid #ddd', objectFit: 'cover', maxWidth: '100%' }} onError={e => { e.target.style.display = 'none'; }} />
                  )}
                </div>

                {/* ── Page Hero Background Images Section ── */}
                <hr style={{ margin: '32px 0 18px', border: 'none', borderTop: '1px solid #e8e8e8' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1a5276', display: 'flex', alignItems: 'center', gap: 8 }}>
                      🌄 पृष्ठ शीर्षलेख पार्श्वभूमी चित्रे (Page Header Hero Images)
                    </h3>
                    <p style={{ margin: '4px 0 0', color: '#666', fontSize: '0.82rem' }}>
                      वेबसाइटवरील प्रत्येक मुख्य पृष्ठाच्या शीर्षलेख (Hero Section) साठी स्वतंत्र पार्श्वभूमी प्रतिमा निवडा किंवा अपलोड करा. प्रतिमा रिकामी ठेवल्यास मूळ डीफॉल्ट प्रतिमा दिसेल.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => savePageHeroImages()}
                    disabled={savingHeroImages}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', fontSize: '0.88rem' }}
                  >
                    {savingHeroImages ? 'जतन होत आहे...' : '💾 सर्व पार्श्वभूमी चित्रे जतन करा'}
                  </button>
                </div>

                <input
                  ref={heroFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleHeroImageFile}
                />

                <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
                  {FIXED_HERO_PAGES.map((p) => {
                    const customUrl = pageHeroImages[p.key] || '';
                    const displayImage = customUrl || p.defaultImage;
                    const isCustom = Boolean(customUrl);
                    const isUploading = heroUploadingKey === p.key;

                    return (
                      <div
                        key={p.key}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '150px 1fr auto',
                          gap: 16,
                          alignItems: 'center',
                          background: isCustom ? '#f0f9ff' : '#ffffff',
                          border: isCustom ? '1.5px solid #0284c7' : '1px solid #e2e8f0',
                          borderRadius: 10,
                          padding: '12px 16px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                        }}
                      >
                        {/* Thumbnail Preview */}
                        <div style={{ position: 'relative', width: 150, height: 78, borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e1', background: '#0f172a' }}>
                          <img
                            src={displayImage}
                            alt={p.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.style.opacity = '0.3'; }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: isCustom ? '#0284c7' : 'rgba(15,23,42,0.75)',
                              color: '#ffffff',
                            }}
                          >
                            {isCustom ? 'कस्टम' : 'डीफॉल्ट'}
                          </span>
                        </div>

                        {/* Page Information & Input */}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>{p.title}</strong>
                            <code style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>{p.route}</code>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="text"
                              value={customUrl}
                              onChange={e => updateHeroImageUrl(p.key, e.target.value)}
                              placeholder={`डीफॉल्ट: ${p.defaultImage}`}
                              style={{ flex: 1, padding: '6px 10px', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 6 }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => {
                                setActiveUploadPageKey(p.key);
                                setTimeout(() => heroFileInputRef.current?.click(), 50);
                              }}
                              disabled={isUploading}
                              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              {isUploading ? 'अपलोड होत आहे...' : '📷 अपलोड करा'}
                            </button>
                            {isCustom && (
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => resetHeroImage(p.key)}
                                title="डीफॉल्ट प्रतिमेवर रीसेट करा"
                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#64748b', whiteSpace: 'nowrap' }}
                              >
                                ↺ डीफॉल्ट
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Quick Save button */}
                        <div>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => savePageHeroImages()}
                            disabled={savingHeroImages}
                            title="या पृष्ठाचे बदल जतन करा"
                            style={{ padding: '7px 12px', fontSize: '0.8rem', color: '#1a5276', borderColor: '#1a5276' }}
                          >
                            जतन करा
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </form>
            )}

            {/* ── REPRESENTATIVES ──────────────────────────────────────── */}
            {tab === 'representatives' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, color: '#1a5276' }}>👤 लोकप्रतिनिधी — Political Representatives</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => { setRepForm(BLANK_REP); setEditRepIdx(null); setShowRepForm(s => !s); }}>
                    {showRepForm ? 'Cancel' : '+ Add Representative'}
                  </button>
                </div>
                {showRepForm && (
                  <form onSubmit={handleRepSubmit} style={cardStyle}>
                    <h4 style={{ marginTop: 0 }}>{editRepIdx !== null ? 'Edit Representative' : 'New Representative'}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div className="form-group"><label>Name / नाव *</label><input required value={repForm.name} onChange={e => setRepForm({ ...repForm, name: e.target.value })} /></div>
                      <div className="form-group">
                        <label>Category / श्रेणी</label>
                        <select value={repForm.category} onChange={e => setRepForm({ ...repForm, category: e.target.value })}>
                          {REP_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Designation / पद *</label><input required value={repForm.designation} onChange={e => setRepForm({ ...repForm, designation: e.target.value })} /></div>
                      <div className="form-group" style={{ gridColumn: 'span 2' }}>
                        <label>Photo URL or Upload (optional)</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input value={repForm.photo} onChange={e => setRepForm({ ...repForm, photo: e.target.value })} placeholder="https://..." style={{ flex: 1 }} />
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => repFileInputRef.current?.click()}
                            disabled={repUploading}
                          >{repUploading ? 'Uploading...' : 'Upload'}</button>
                        </div>
                        <input ref={repFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleRepImageFile} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="submit" className="btn btn-primary btn-sm">💾 Save</button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowRepForm(false); setRepForm(BLANK_REP); setEditRepIdx(null); }}>Cancel</button>
                    </div>
                  </form>
                )}
                {!showRepForm && (reps.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>No representatives added yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {reps.map((rep, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'white', border: '1px solid #e8e8e8', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ width: 50, height: 50, borderRadius: '50%', overflow: 'hidden', border: '2px solid #1a5276', flexShrink: 0 }}>
                          <img
                            src={rep.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(rep.name)}&size=50&background=1a5276&color=fff&bold=true`}
                            alt={rep.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(rep.name)}&size=50&background=1a5276&color=fff&bold=true`; }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <strong>{rep.name}</strong>
                          <p style={{ margin: '2px 0 0', color: '#666', fontSize: '0.85rem' }}>{rep.designation}</p>
                          <span style={{ fontSize: '0.75rem', background: '#eef2ff', color: '#3b5bdb', borderRadius: 8, padding: '2px 8px', marginTop: 4, display: 'inline-block' }}>{rep.category}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => moveRep(idx, -1)} disabled={idx === 0}>↑</button>
                          <button className="btn btn-outline btn-sm" onClick={() => moveRep(idx, 1)} disabled={idx === reps.length - 1}>↓</button>
                          <button className="btn btn-outline btn-sm" onClick={() => startEditRep(rep, idx)}>✏️</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteRep(idx)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* ── PAGES ────────────────────────────────────────────────── */}
            {tab === 'pages' && (
              <>
                <div style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowPageForm(s => !s); setEditingPage(null); setPageForm(BLANK_PAGE); setPageSections([{ id: Date.now(), heading: '', content: '', order: 0 }]); }}>
                    {showPageForm && !editingPage ? 'Cancel' : '+ Add Page'}
                  </button>
                </div>
                {showPageForm && (
                  <form onSubmit={savePage} style={{ marginBottom: 20, padding: 16, background: '#f9f9f9', borderRadius: 8, border: '1px solid #eee' }}>
                    <h4 style={{ marginTop: 0 }}>{editingPage ? `Edit: ${editingPage.title}` : 'New Page'}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div className="form-group"><label>Title *</label><input value={pageForm.title} onChange={e => setPageForm({ ...pageForm, title: e.target.value })} required /></div>
                      <div className="form-group">
                        <label>Slug * {editingPage && <span style={{ color: '#888', fontSize: '0.78rem' }}>(slug cannot be changed)</span>}</label>
                        <input value={pageForm.slug} onChange={e => setPageForm({ ...pageForm, slug: e.target.value })} required disabled={!!editingPage} />
                      </div>
                      <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" id="pg-pub" checked={pageForm.is_published} onChange={e => setPageForm({ ...pageForm, is_published: e.target.checked })} style={{ width: 16, height: 16 }} />
                        <label htmlFor="pg-pub" style={{ margin: 0 }}>Published</label>
                      </div>
                    </div>

                    {/* Dynamic Sections */}
                    <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a5276', marginBottom: 8, display: 'block' }}>विभाग (Sections)</label>
                    {pageSections.map((sec, idx) => (
                      <div key={sec.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                          <span style={{ background: '#e67e22', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                          <input
                            value={sec.heading}
                            onChange={e => updatePageSection(sec.id, 'heading', e.target.value)}
                            placeholder="विभाग शीर्षक (Section heading)"
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #d0d8e0', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600 }}
                          />
                          <div style={{ display: 'flex', gap: 3 }}>
                            <button type="button" title="वर" disabled={idx === 0} onClick={() => movePageSection(idx, -1)}
                              style={{ padding: '3px 7px', border: '1px solid #ccc', borderRadius: 4, cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.4 : 1, background: 'white' }}>▲</button>
                            <button type="button" title="खाली" disabled={idx === pageSections.length - 1} onClick={() => movePageSection(idx, 1)}
                              style={{ padding: '3px 7px', border: '1px solid #ccc', borderRadius: 4, cursor: idx === pageSections.length - 1 ? 'default' : 'pointer', opacity: idx === pageSections.length - 1 ? 0.4 : 1, background: 'white' }}>▼</button>
                            <button type="button" title="हटवा" onClick={() => deletePageSection(sec.id)}
                              style={{ padding: '3px 7px', border: '1px solid #e57373', borderRadius: 4, cursor: pageSections.length <= 1 ? 'default' : 'pointer', opacity: pageSections.length <= 1 ? 0.4 : 1, background: '#fde8e8', color: '#c62828' }}>🗑️</button>
                          </div>
                        </div>
                        <RichTextEditor value={sec.content} onChange={v => updatePageSection(sec.id, 'content', v)} placeholder="विभाग माहिती..." tenantSlug={slug} />
                      </div>
                    ))}
                    <button type="button" onClick={addPageSection}
                      style={{ width: '100%', padding: '10px', background: '#f0f8ff', border: '2px dashed #a8d4f0', borderRadius: 8, cursor: 'pointer', color: '#1a5276', fontWeight: 600, fontSize: '0.85rem', marginBottom: 14 }}>
                      ➕ नवीन विभाग जोडा
                    </button>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="submit" className="btn btn-primary btn-sm">💾 {editingPage ? 'Update' : 'Create'} Page</button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowPageForm(false); setEditingPage(null); }}>Cancel</button>
                    </div>
                  </form>
                )}
                <table className="data-table" hidden={showPageForm}>
                  <thead>
                    <tr><th>Title</th><th>Slug</th><th>Type</th><th>Published</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {visiblePages.map((p) => (
                      <tr key={p.id}>
                        <td>{p.title}</td>
                        <td><code style={{ fontSize: '0.8rem', background: '#f5f5f5', padding: '2px 5px', borderRadius: 3 }}>{p.slug}</code></td>
                        <td><span style={{ fontSize: '0.75rem', color: SYSTEM_SLUGS.has(p.slug) ? '#888' : '#0066cc' }}>{SYSTEM_SLUGS.has(p.slug) ? 'System' : 'Custom'}</span></td>
                        <td>
                          <button className={`btn btn-sm ${p.is_published ? 'btn-success' : 'btn-outline'}`} style={{ minWidth: 72 }} onClick={() => togglePage(p)}>
                            {p.is_published ? '✓ Yes' : '○ No'}
                          </button>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-outline btn-sm" onClick={() => startEditPage(p)}>✏️ Edit</button>
                            {!SYSTEM_SLUGS.has(p.slug) && (
                              <button className="btn btn-danger btn-sm" onClick={() => deletePage(p.id)}>🗑️</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── GALLERY ──────────────────────────────────────────────── */}
            {tab === 'gallery' && (
              <GalleryAdminPanel slug={slug} items={gallery} onReload={loadData} onMessage={flash} />
            )}

            {/* ── FEES ─────────────────────────────────────────────────── */}
            {tab === 'fees' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, color: '#1a5276' }}>💰 Service Fees</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowFeeForm(s => !s)}>
                    {showFeeForm ? 'Cancel' : '+ Add Fee'}
                  </button>
                </div>
                {showFeeForm && (
                  <form onSubmit={addNewFee} style={cardStyle}>
                    <h4 style={{ marginTop: 0 }}>Add / Update Fee</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <div className="form-group">
                        <label>Service Type *</label>
                        <input list="svc-types" required value={newFeeForm.service_type} onChange={e => setNewFeeForm({ ...newFeeForm, service_type: e.target.value })} placeholder="birth, death, marriage..." />
                        <datalist id="svc-types">
                          {['birth', 'death', 'marriage', 'residence', 'income', 'caste', 'character', 'migration', 'bpl', 'disability', 'senior_citizen'].map(t => <option key={t} value={t} />)}
                        </datalist>
                      </div>
                      <div className="form-group"><label>Service Name *</label><input required value={newFeeForm.service_name} onChange={e => setNewFeeForm({ ...newFeeForm, service_name: e.target.value })} placeholder="e.g. Birth Certificate" /></div>
                      <div className="form-group"><label>Fee Amount (₹) *</label><input type="number" min="0" step="0.01" required={!newFeeForm.contact_gp_for_fees} disabled={newFeeForm.contact_gp_for_fees} value={newFeeForm.contact_gp_for_fees ? '' : newFeeForm.fee_amount} onChange={e => setNewFeeForm({ ...newFeeForm, fee_amount: e.target.value })} placeholder={newFeeForm.contact_gp_for_fees ? "ग्रामपंचायतीशी संपर्क" : "0.00"} /></div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input type="checkbox" checked={newFeeForm.contact_gp_for_fees} onChange={e => setNewFeeForm({ ...newFeeForm, contact_gp_for_fees: e.target.checked, fee_amount: e.target.checked ? '0' : newFeeForm.fee_amount })} />
                          <span>शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा (Contact to GP for fee details)</span>
                        </label>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="submit" className="btn btn-primary btn-sm">Save Fee</button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowFeeForm(false); setNewFeeForm({ service_type: '', service_name: '', fee_amount: '' }); }}>Cancel</button>
                    </div>
                  </form>
                )}

                {/* Edit Fee Modal */}
                {editingFee && (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <form onSubmit={saveEditingFee} style={{ background: 'white', borderRadius: 12, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                      <h3 style={{ marginTop: 0, color: '#1a5276' }}>✏️ Edit Fee: {editingFee.service_type?.toUpperCase()}</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group"><label>Service Name</label><input value={editingFee.service_name} onChange={e => setEditingFee({ ...editingFee, service_name: e.target.value })} /></div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={editingFee.contact_gp_for_fees}
                              onChange={e => setEditingFee({ ...editingFee, contact_gp_for_fees: e.target.checked, fee_amount: e.target.checked ? '0' : editingFee.fee_amount, duplicate_fee: e.target.checked ? '' : editingFee.duplicate_fee })}
                            />
                            <span>शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा (Contact to GP for fee details)</span>
                          </label>
                        </div>
                        <div className="form-group"><label>Fee Amount (₹)</label><input type="number" min="0" step="0.01" disabled={editingFee.contact_gp_for_fees} value={editingFee.contact_gp_for_fees ? '' : editingFee.fee_amount} onChange={e => setEditingFee({ ...editingFee, fee_amount: e.target.value })} placeholder={editingFee.contact_gp_for_fees ? "ग्रामपंचायतीशी संपर्क" : "0.00"} /></div>
                        <div className="form-group"><label>Duplicate Copy Fee (₹)</label><input type="number" min="0" step="0.01" disabled={editingFee.contact_gp_for_fees} value={editingFee.contact_gp_for_fees ? '' : editingFee.duplicate_fee} onChange={e => setEditingFee({ ...editingFee, duplicate_fee: e.target.value })} placeholder={editingFee.contact_gp_for_fees ? "लागू नाही" : "Optional"} /></div>
                      </div>
                      <h4 style={{ marginBottom: 10 }}>Exemptions (% discount off fee)</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        {EXEMPTION_KEYS.map(k => (
                          <div key={k} className="form-group">
                            <label>{fmtExKey(k)} (%)</label>
                            <input type="number" min="0" max="100" value={editingFee.exemptions[k]} onChange={e => setEditingFee({ ...editingFee, exemptions: { ...editingFee.exemptions, [k]: e.target.value } })} placeholder="0 = no exemption, 100 = free" />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button type="submit" className="btn btn-primary btn-sm">💾 Save Changes</button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditingFee(null)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                <table className="data-table" hidden={showFeeForm || Boolean(editingFee)}>
                  <thead>
                    <tr><th>Service</th><th>Fee</th><th>Duplicate</th><th>Exemptions</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {fees.map((f) => (
                      <tr key={f.id}>
                        <td>
                          <strong>{f.service_type?.replace(/_/g, ' ').toUpperCase()}</strong>
                          <p style={{ margin: '2px 0 0', color: '#888', fontSize: '0.8rem' }}>{f.service_name}</p>
                        </td>
                        <td>{f.contact_gp_for_fees ? <span style={{ color: '#0284c7', fontWeight: 600 }}>ग्रामपंचायतीशी संपर्क</span> : formatMarathiCurrency(f.fee_amount)}</td>
                        <td>{f.duplicate_fee ? formatMarathiCurrency(f.duplicate_fee) : '—'}</td>
                        <td>
                          <div style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.6 }}>
                            {formatExemptions(f.exemptions) || <span style={{ color: '#bbb' }}>None</span>}
                          </div>
                        </td>
                        <td><button className="btn btn-outline btn-sm" onClick={() => startEditFee(f)}>✏️ Edit</button></td>
                      </tr>
                    ))}
                    {fees.length === 0 && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No fees configured yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {/* ── MESSAGES ─────────────────────────────────────────────── */}
            {tab === 'messages' && (
              <div style={{ marginTop: 10 }}>
                <AdminContactMessages />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
